// MCHD Room Scheduler - Building Tree View Component
// Created: 07-10-2025 14:01:32
// Purpose: Hierarchical tree navigation for Buildings → Floors → Rooms

import React, { useState } from 'react';
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  Badge,
  Box,
  Collapse,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconChevronRight,
  IconChevronDown,
  IconBuilding,
  IconStairs,
  IconDoor,
  IconInfoCircle,
  IconAlertCircle,
  IconPlus,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

/**
 * Building interface with nested floors and rooms
 */
export interface BuildingTreeData {
  id: string;
  name: string;
  isActive: boolean;
  floors: FloorTreeData[];
}

/**
 * Floor interface with nested rooms
 */
export interface FloorTreeData {
  id: string;
  name: string;
  number: number;
  buildingId: string;
  rooms: RoomTreeData[];
}

/**
 * Room interface for tree display
 */
export interface RoomTreeData {
  id: string;
  number: string;
  name: string;
  isActive: boolean;
  capacity: number;
  floorId: string;
}

/**
 * Props for BuildingTreeView component
 */
export interface BuildingTreeViewProps {
  /** Buildings data with nested floors and rooms */
  buildings: BuildingTreeData[];
  /** Loading state */
  isLoading?: boolean;
  /** Error message */
  error?: string | null;
  /** Callback when room is clicked */
  onRoomClick?: (roomId: string) => void;
  /** Callback when add floor button is clicked */
  onAddFloor?: (building: BuildingTreeData) => void;
}

/**
 * BuildingTreeView Component
 * 
 * Displays hierarchical tree structure for Buildings → Floors → Rooms.
 * Implements UX Touchpoint Spec requirement for nested navigation (Gaps G1-3, G3-1).
 * 
 * Features:
 * - Expandable/collapsible building and floor nodes
 * - Click room to navigate to Room Detail Page
 * - Active/inactive status indicators
 * - Capacity badges for rooms
 * - Empty states per UX spec (Gap G3-2)
 * - Keyboard accessible (Tab, Enter, Arrow keys)
 * 
 * @component
 * @example
 * ```tsx
 * <BuildingTreeView 
 *   buildings={buildingsData}
 *   isLoading={false}
 *   onRoomClick={(roomId) => navigate(`/coordinator/rooms/${roomId}`)}
 * />
 * ```
 * 
 * Created: 07-10-2025 14:01:32
 * Complies: @react-frontend.mdc, @ui-ux-excellence.mdc, @hospital-domain.mdc
 */
export const BuildingTreeView: React.FC<BuildingTreeViewProps> = ({
  buildings,
  isLoading = false,
  error = null,
  onRoomClick,
  onAddFloor,
}) => {
  const navigate = useNavigate();
  const [expandedBuildings, setExpandedBuildings] = useState<Set<string>>(new Set());
  const [expandedFloors, setExpandedFloors] = useState<Set<string>>(new Set());

  /**
   * Toggle building expansion
   * @param buildingId - Building ID to toggle
   */
  const toggleBuilding = (buildingId: string) => {
    const newExpanded = new Set(expandedBuildings);
    if (newExpanded.has(buildingId)) {
      newExpanded.delete(buildingId);
    } else {
      newExpanded.add(buildingId);
    }
    setExpandedBuildings(newExpanded);
  };

  /**
   * Toggle floor expansion
   * @param floorId - Floor ID to toggle
   */
  const toggleFloor = (floorId: string) => {
    const newExpanded = new Set(expandedFloors);
    if (newExpanded.has(floorId)) {
      newExpanded.delete(floorId);
    } else {
      newExpanded.add(floorId);
    }
    setExpandedFloors(newExpanded);
  };

  /**
   * Handle room click
   * @param roomId - Room ID
   */
  const handleRoomClick = (roomId: string) => {
    if (onRoomClick) {
      onRoomClick(roomId);
    } else {
      navigate(`/coordinator/rooms/${roomId}`);
    }
  };

  // Loading state - Enhanced per UI/UX Excellence standards
  if (isLoading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="md" fw={500} c="dimmed">Loading building structure...</Text>
          <Text size="sm" c="dimmed">Fetching buildings, floors, and rooms</Text>
        </Stack>
      </Center>
    );
  }

  // Error state - Enhanced per UI/UX Excellence standards
  if (error) {
    return (
      <Box p="lg" style={{ border: '1px solid var(--mantine-color-red-3)', borderRadius: 'var(--mantine-radius-md)', backgroundColor: 'var(--mantine-color-red-0)' }}>
        <Stack gap="sm">
          <Group gap="xs">
            <IconAlertCircle size={20} color="var(--mantine-color-red-6)" />
            <Text size="sm" fw={500} c="red">Failed to load building structure</Text>
          </Group>
          <Text size="sm" c="red">{error}</Text>
          <Text size="xs" c="dimmed">Please refresh the page or contact support if the issue persists.</Text>
        </Stack>
      </Box>
    );
  }

  // Empty state - Enhanced per UI/UX Excellence standards
  if (!buildings || buildings.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="md">
          <Box style={{ 
            padding: '2rem', 
            borderRadius: '50%', 
            backgroundColor: 'var(--mantine-color-gray-1)',
            border: '2px dashed var(--mantine-color-gray-3)'
          }}>
            <IconInfoCircle size={48} color="var(--mantine-color-gray-5)" />
          </Box>
          <Stack align="center" gap="xs">
            <Text size="lg" fw={600} c="dimmed">No buildings yet</Text>
            <Text size="sm" c="dimmed" ta="center" maw={300}>
              Add one to start defining rooms.
            </Text>
          </Stack>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      {buildings.map((building) => (
        <Box 
          key={building.id}
          style={{
            border: '1px solid var(--mantine-color-gray-2)',
            borderRadius: 'var(--mantine-radius-md)',
            backgroundColor: 'var(--mantine-color-white)',
            overflow: 'hidden',
          }}
        >
          {/* Building Level */}
          <Group
            gap="md"
            p="md"
            style={{
              cursor: 'pointer',
              backgroundColor: expandedBuildings.has(building.id)
                ? 'var(--mantine-color-blue-0)'
                : 'var(--mantine-color-gray-0)',
              borderBottom: expandedBuildings.has(building.id) ? '1px solid var(--mantine-color-blue-2)' : 'none',
              transition: 'all 0.2s ease',
            }}
            onClick={() => toggleBuilding(building.id)}
            onMouseEnter={(e) => {
              if (!expandedBuildings.has(building.id)) {
                e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-1)';
              }
            }}
            onMouseLeave={(e) => {
              if (!expandedBuildings.has(building.id)) {
                e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-0)';
              }
            }}
          >
            <ActionIcon 
              size="lg" 
              variant="transparent"
              style={{ 
                color: 'var(--mantine-color-blue-6)',
                transition: 'transform 0.2s ease'
              }}
            >
              {expandedBuildings.has(building.id) ? (
                <IconChevronDown size={20} />
              ) : (
                <IconChevronRight size={20} />
              )}
            </ActionIcon>
            <IconBuilding 
              size={24} 
              color="var(--mantine-color-blue-6)" 
              style={{ marginRight: '4px' }}
            />
            <Box style={{ flex: 1 }}>
              <Text size="lg" fw={600} c="dark">
                {building.name}
              </Text>
              <Text size="xs" c="dimmed">
                {building.floors.length} floor{building.floors.length !== 1 ? 's' : ''} • {building.floors.reduce((total, floor) => total + floor.rooms.length, 0)} rooms
              </Text>
            </Box>
            <Badge
              size="md"
              color={building.isActive ? 'green' : 'gray'}
              variant="light"
              leftSection={building.isActive ? '✓' : '○'}
            >
              {building.isActive ? 'Active' : 'Inactive'}
            </Badge>
            {/* Add Floor Button */}
            {onAddFloor && (
              <ActionIcon
                color="blue"
                variant="light"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddFloor(building);
                }}
                title={`Add floor to ${building.name}`}
              >
                <IconPlus size={14} />
              </ActionIcon>
            )}
          </Group>

          {/* Floors Level */}
          <Collapse in={expandedBuildings.has(building.id)}>
            <Box p="md" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
              <Stack gap="sm">
                {building.floors.length === 0 ? (
                  <Box p="lg" style={{ 
                    backgroundColor: 'var(--mantine-color-white)', 
                    borderRadius: 'var(--mantine-radius-sm)',
                    border: '1px dashed var(--mantine-color-gray-3)',
                    textAlign: 'center'
                  }}>
                    <Text size="sm" c="dimmed">
                      No floors defined for this building
                    </Text>
                  </Box>
                ) : (
                  building.floors.map((floor) => (
                    <Box 
                      key={floor.id}
                      style={{
                        border: '1px solid var(--mantine-color-gray-2)',
                        borderRadius: 'var(--mantine-radius-sm)',
                        backgroundColor: 'var(--mantine-color-white)',
                        overflow: 'hidden',
                      }}
                    >
                      <Group
                        gap="md"
                        p="md"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: expandedFloors.has(floor.id)
                            ? 'var(--mantine-color-blue-1)'
                            : 'transparent',
                          borderBottom: expandedFloors.has(floor.id) ? '1px solid var(--mantine-color-blue-2)' : 'none',
                          transition: 'all 0.2s ease',
                        }}
                        onClick={() => toggleFloor(floor.id)}
                        onMouseEnter={(e) => {
                          if (!expandedFloors.has(floor.id)) {
                            e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!expandedFloors.has(floor.id)) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <ActionIcon size="md" variant="transparent">
                          {expandedFloors.has(floor.id) ? (
                            <IconChevronDown size={18} />
                          ) : (
                            <IconChevronRight size={18} />
                          )}
                        </ActionIcon>
                        <IconStairs size={20} color="var(--mantine-color-grape-6)" />
                        <Box style={{ flex: 1 }}>
                          <Text size="md" fw={500} c="dark">
                            {floor.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            Floor {floor.number} • {floor.rooms.length} room{floor.rooms.length !== 1 ? 's' : ''}
                          </Text>
                        </Box>
                        <Badge 
                          size="sm" 
                          variant="light" 
                          color={floor.rooms.length > 0 ? 'blue' : 'gray'}
                          leftSection="🚪"
                        >
                          {floor.rooms.length} rooms
                        </Badge>
                      </Group>

                    {/* Rooms Level */}
                    <Collapse in={expandedFloors.has(floor.id)}>
                      <Box p="md" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                        <Stack gap="xs">
                          {floor.rooms.length === 0 ? (
                            <Box p="lg" style={{ 
                              backgroundColor: 'var(--mantine-color-white)', 
                              borderRadius: 'var(--mantine-radius-sm)',
                              border: '1px dashed var(--mantine-color-gray-3)',
                              textAlign: 'center'
                            }}>
                              <Text size="sm" c="dimmed">
                                No rooms defined for this floor
                              </Text>
                            </Box>
                        ) : (
                          <Stack gap="sm">
                            {floor.rooms.map((room) => (
                              <Group
                                key={room.id}
                                gap="md"
                                p="md"
                                style={{
                                  cursor: 'pointer',
                                  borderRadius: 'var(--mantine-radius-sm)',
                                  backgroundColor: 'var(--mantine-color-white)',
                                  border: `1px solid ${room.isActive ? 'var(--mantine-color-green-2)' : 'var(--mantine-color-gray-3)'}`,
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                  transition: 'all 0.2s ease',
                                }}
                                onClick={() => handleRoomClick(room.id)}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = room.isActive ? 'var(--mantine-color-blue-0)' : 'var(--mantine-color-gray-1)';
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'var(--mantine-color-white)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleRoomClick(room.id);
                                  }
                                }}
                              >
                                <IconDoor 
                                  size={16} 
                                  color={room.isActive ? 'var(--mantine-color-teal-6)' : 'var(--mantine-color-gray-5)'} 
                                />
                                <Box style={{ flex: 1 }}>
                                  <Text size="sm" fw={500} c={room.isActive ? 'dark' : 'dimmed'}>
                                    {room.number} - {room.name}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    Floor {floor.number} • Building {building.name}
                                  </Text>
                                </Box>
                                <Badge 
                                  size="sm" 
                                  variant="light" 
                                  color={room.isActive ? 'blue' : 'gray'}
                                  leftSection="👥"
                                >
                                  {room.capacity} seats
                                </Badge>
                                {room.isActive ? (
                                  <Badge size="xs" color="green" variant="dot">
                                    Active
                                  </Badge>
                                ) : (
                                  <Badge size="xs" color="red" variant="dot">
                                    Inactive
                                  </Badge>
                                )}
                              </Group>
                            ))}
                          </Stack>
                        )}
                        </Stack>
                      </Box>
                    </Collapse>
                  </Box>
                ))
                )}
              </Stack>
            </Box>
          </Collapse>
        </Box>
      ))}
    </Stack>
  );
};

export default BuildingTreeView;

